import { getFederalHolidayInfo } from '../../utils/isFederalHoliday';
import { BASE_URL } from '../../constants';

// Application identifier used by the dynamic outage warning endpoint.
export const APPLICATION_TARGET = 'MarketingWebsite';

// Maps the API notification_type to a MUI Alert severity.
// 3: Information, 4: Warning, 5: Outage
const NOTIFICATION_TYPE_SEVERITY = {
  3: 'info',
  4: 'warning',
  5: 'error'
};

/**
 * Maps an outage warning record from the API to the GlobalWarning config shape.
 * @param {Object} warning - A single outage warning record from the endpoint
 * @returns {Object} Warning configuration object
 */
const mapOutageWarning = warning => {
  const { notification_type, title, body, effective_date } = warning;
  const message = title && body ? `${title}: ${body}` : body || title || '';

  return {
    message,
    severity: NOTIFICATION_TYPE_SEVERITY[notification_type] || 'warning',
    dismissible: true,
    // Unique per warning so dismissing one does not hide future warnings.
    storageKey: `outageWarning_${effective_date || title || message}`
  };
};

/**
 * Fetches the current outage warning from the dynamic endpoint.
 * @param {number} [notificationType] - Optional notification type filter.
 *   1: Mass Notification, 2: User Notification, 3: Information,
 *   4: Warning, 5: Outage. Omit/null/0 for all.
 * @returns {Promise<Object|null>} The active warning config or null
 */
export const fetchOutageWarning = async notificationType => {
  try {
    const response = await fetch(`${BASE_URL}/Marketing/GetCurrentOutageWarning`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        application_target: APPLICATION_TARGET,
        ...(notificationType ? { notification_type: notificationType } : {})
      })
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    const records = Array.isArray(data) ? data : [data];
    const active = records.find(record => record && record.has_warning);

    return active ? mapOutageWarning(active) : null;
  } catch (error) {
    // Fail silently so a transient API error never breaks the page.
    return null;
  }
};

/**
 * Creates a federal holiday warning configuration.
 * @param {string} holidayName - The name of the federal holiday
 * @param {boolean} isAdvanceNotice - Whether this is advance notice (day before)
 * @returns {Object} Warning configuration object
 */
export const createFederalHolidayWarning = (holidayName, isAdvanceNotice = false) => {
  const message = isAdvanceNotice
    ? `Our offices will be closed tomorrow in observance of ${holidayName}. We will resume normal business hours on the following business day.`
    : `Our offices are closed today in observance of ${holidayName}. We will resume normal business hours on the next business day.`;

  return {
    message,
    severity: 'info',
    dismissible: true,
    storageKey: `federalHoliday_${holidayName.replace(/\s+/g, '_')}_${
      isAdvanceNotice ? 'advance' : 'today'
    }`
  };
};

/**
 * Gets the active warning to display.
 * Checks the dynamic outage endpoint first, then falls back to the
 * automatic federal holiday notice.
 * @param {number} [notificationType] - Optional notification type filter.
 * @returns {Promise<Object|null>} The active warning configuration or null
 */
export const getActiveWarning = async notificationType => {
  // Dynamic outage warnings take priority.
  const outageWarning = await fetchOutageWarning(notificationType);
  if (outageWarning) {
    return outageWarning;
  }

  // Fall back to the automatic federal holiday notice.
  const holidayInfo = getFederalHolidayInfo(new Date());
  if (holidayInfo) {
    return createFederalHolidayWarning(holidayInfo.name, holidayInfo.isAdvanceNotice);
  }

  return null;
};